<template>
  <v-container fluid class="pa-0 initial-setup-page">
    <v-container class="py-2 pt-md-6 mx-auto" style="max-width: 880px;">
      <v-card rounded="lg" elevation="0" border>
        <v-progress-linear v-if="loading || saving" indeterminate color="primary" />
        <div class="pa-3 pa-md-4 setup-header">
          <div class="d-flex align-center ga-2 mb-1">
            <v-icon size="18" color="primary">mdi-star-circle-outline</v-icon>
            <div class="text-caption text-uppercase font-weight-bold text-primary">{{ $t("initialsetup.badge") }}</div>
          </div>
          <h2 class="text-h6 mb-0 mt-3">{{ $t("initialsetup.title") }}</h2>
          <p class="text-body-2 text-medium-emphasis mb-0 mt-3">
            {{ $t("initialsetup.intro") }} <br>
          </p>
        </div>

        <div class="pa-3 pa-md-4">
          <div class="onboarding-layout">
            <v-card class="onboarding-sidebar pa-4" rounded="lg" flat>
              <div v-for="(step, index) in steps" :key="step.value"
                class="d-flex align-start ga-3 py-2 cursor-pointer onboarding-step-item" @click="goToStep(step.value)">
                <div class="step-marker-wrap">
                  <v-avatar size="24"
                    :color="getStepState(step.value) === 'done' ? 'success' : (getStepState(step.value) === 'active' ? 'primary' : 'white')">
                    <v-icon v-if="getStepState(step.value) === 'done'" size="14" color="white">mdi-check</v-icon>
                    <span v-else
                      :class="getStepState(step.value) === 'active' ? 'text-white text-caption' : 'text-caption text-medium-emphasis'">
                      {{ step.value }}
                    </span>
                  </v-avatar>
                  <div v-if="index !== steps.length - 1" class="step-marker-line" />
                </div>
                <div>
                  <div class="text-body-2 font-weight-bold">{{ step.title }}</div>
                </div>
              </div>
            </v-card>

            <v-card class="onboarding-content pa-4" rounded="lg" flat>
              <div class="text-caption  font-weight-bold text-primary mb-2" style="font-size: 13px;">
                {{ $t("initialsetup.step_of", { step: currentStep, total: steps.length }) }}
              </div>
              <h3 class="mb-0 mt-2">
                {{ currentStepMeta.title }}
              </h3>
              <p class="text-body-2 text-medium-emphasis mt-2 mb-4" style="font-size: 14px;">
                {{ currentStepMeta.description }}
              </p>

              <template v-if="currentStep === '1'">
                <v-row class="py-1" no-gutters>
                  <v-col cols="12">
                    <v-row class="ga-0">
                      <v-col v-for="option in languageOptions" :key="option.value" cols="12" sm="6" md="4" class="pa-2">
                        <v-card
                          class="language-option-card d-flex flex-column align-center justify-center text-center py-4 px-2"
                          :class="{ 'language-option-card--selected': form.language === option.value }" rounded="lg"
                          border flat @click="setSelectedLanguage(option.value)">
                          <v-icon v-if="form.language === option.value" size="16" color="primary"
                            class="language-option-selected-icon">
                            mdi-check-circle
                          </v-icon>
                          <img v-if="hasFlag(option.value)" class="language-flag-image mb-2"
                            :src="getFlagSrc(option.value)" alt="" />
                          <span v-else class="language-code-fallback mb-2">{{ option.value }}</span>
                          <div class="text-body-2">{{ option.label }}</div>
                        </v-card>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </template>

              <template v-else-if="currentStep === '2'">
                <v-row class="py-1" align="center" no-gutters>
                  <v-col cols="12" md="5">
                    <div class="text-body-2 font-weight-medium">{{ $t("initialsetup.company_logo") }}</div>
                  </v-col>
                  <v-col cols="12" md="7" class="d-flex flex-column align-end">
                    <div style="height: 90px; width: 250px;">
                      <input ref="logoFileInput" type="file" accept="image/*" class="d-none"
                        @change="onLogoFileSelected">
                      <div class="logo-upload-dropzone" :class="{ 'logo-upload-dropzone--has-image': !!logoPreviewUrl }"
                        @click="openLogoPicker">
                        <template v-if="logoPreviewUrl">
                          <v-img :src="logoPreviewUrl" contain class="logo-upload-preview" />
                          <div class="logo-upload-overlay text-caption" style="font-size: 12px;">{{
                            $t("initialsetup.logo_change") }}</div>
                        </template>
                        <template v-else>
                          <v-icon size="34" color="primary">mdi-image-outline</v-icon>
                          <div class="text-body-2 mt-2">
                            {{ $t("initialsetup.logo_drop_prefix") }}
                            <span class="text-primary font-weight-medium">{{ $t("initialsetup.logo_browse") }}</span>
                          </div>
                        </template>
                      </div>
                    </div>
                  </v-col>
                </v-row>
                <v-divider class="my-2" />

                <v-row class="py-1" align="center" no-gutters>
                  <v-col cols="12" md="5">
                    <div class="text-body-2 font-weight-medium">{{ $t("initialsetup.company_name") }}</div>
                  </v-col>
                  <v-col cols="12" md="7">
                    <v-text-field v-model="form.companyName" variant="underlined" density="compact" hide-details />
                  </v-col>
                </v-row>
                <v-divider class="my-2" />

                <v-row class="py-1" align="center" no-gutters>
                  <v-col cols="12" md="5">
                    <div class="text-body-2 font-weight-medium">{{ $t("initialsetup.customer_service_phone") }}</div>
                  </v-col>
                  <v-col cols="12" md="7">
                    <v-text-field v-model="form.telephone" placeholder="+47 00 00 00 00" variant="underlined"
                      density="compact" hide-details />
                  </v-col>
                </v-row>
                <v-divider class="my-2" />

                <v-row class="py-1" align="center" no-gutters>
                  <v-col cols="12" md="5">
                    <div class="text-body-2 font-weight-medium">{{ $t("initialsetup.customer_service_email") }}</div>
                  </v-col>
                  <v-col cols="12" md="7">
                    <v-text-field v-model="form.email" placeholder="support@company.com" variant="underlined"
                      density="compact" hide-details />
                  </v-col>
                </v-row>
              </template>

              <template v-else-if="currentStep === '3'">
                <v-row class="py-1" align="center" no-gutters>
                  <v-col cols="12" md="4">
                    <div class="text-body-2 font-weight-medium">{{ $t("initialsetup.address_street") }}</div>
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-text-field v-model="form.addressStreet" placeholder="Drammensveien 167" variant="underlined"
                      density="compact" hide-details />
                  </v-col>
                </v-row>
                <v-divider class="my-2" />

                <v-row class="py-1" align="center" no-gutters>
                  <v-col cols="12" md="4">
                    <div class="text-body-2 font-weight-medium">{{ $t("initialsetup.address_postal_code_and_city") }}
                    </div>
                  </v-col>
                  <v-col cols="12" md="8">
                    <v-text-field v-model="form.addressZipAndCity" placeholder="0277 Oslo" variant="underlined"
                      density="compact" hide-details />
                  </v-col>
                </v-row>
              </template>

            </v-card>
          </div>

          <div class="d-flex justify-space-between align-center mt-8">
            <small class="text-caption text-medium-emphasis">{{ $t("initialsetup.footer_note") }}</small>
            <div class="d-flex ga-2">
              <v-btn v-if="authToken" variant="text" :disabled="saving" @click="skipSetup">
                {{ $t("initialsetup.skip") }}
              </v-btn>
              <v-btn variant="text" :disabled="isFirstStep || saving" @click="goToPreviousStep">
                {{ $t("initialsetup.back") }}
              </v-btn>
              <v-btn v-if="!isLastStep" color="primary" :disabled="saving" @click="goToNextStep">
                {{ $t("initialsetup.next") }}
              </v-btn>
              <v-btn v-else color="success" :loading="saving" :disabled="saving" @click="saveSetup">
                {{ $t("initialsetup.save") }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-container>

  </v-container>
</template>

<script>
import http from "@/api/client";
import { useUiStore } from "@/store/pinia/uiStore";
import { setStoredLocale } from "@/plugins/i18n/config";
import AuthStoreMixin from "@/helpers/mixins/AuthStoreMixin";
import englishFlag from "@/assets/lang/en.png";
import norwegianFlag from "@/assets/lang/no.png";
import hongkongFlag from "@/assets/lang/zh-HK.png";
import chinaFlag from "@/assets/lang/zh-CN.png";
import swedishFlag from "@/assets/lang/sv.png";

export default {
  name: "InitialSetupLanding",
  mixins: [AuthStoreMixin],
  data() {
    return {
      loading: false,
      saving: false,
      currentStep: "1",
      logoFile: null,
      logoPreviewUrl: "",
      selectedLogoFileName: "",
      form: {
        companyName: "",
        telephone: "",
        email: "",
        addressStreet: "",
        addressZipAndCity: "",
        language: "en-US",
      },
      currentLogoUrl: "/api2/Report/logo",
      languageOptions: [
        //{ label: "English (US)", value: "en-US" },
        { label: "English", value: "en-GB" },
        { label: "Norsk", value: "nb-NO" },
        { label: "Svenska", value: "sv-SE" },
        //{ label: "French", value: "fr-FR" },
        { label: "廣東話", value: "zh-HK" },
        { label: "中国人", value: "zh-CN" },
      ],
      flagImagesByLanguage: {
        "en-GB": englishFlag,
        "nb-NO": norwegianFlag,
        "sv-SE": swedishFlag,
        "zh-HK": hongkongFlag,
        "zh-CN": chinaFlag,
      },
    };
  },
  async mounted() {
    await this.loadInitialSetup();
  },
  beforeUnmount() {
    if (this.logoPreviewUrl && this.logoPreviewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(this.logoPreviewUrl);
    }
  },
  computed: {
    isFirstStep() {
      return this.currentStep === "1";
    },
    isLastStep() {
      return this.currentStep === String(this.steps.length);
    },
    currentStepMeta() {
      return this.steps.find((step) => step.value === this.currentStep) ?? this.steps[0];
    },
    steps() {
      return [
        {
          value: "1",
          title: this.$t("initialsetup.step_language_title"),
          description: this.$t("initialsetup.step_language_description"),
        },
        {
          value: "2",
          title: this.$t("initialsetup.step_company_title"),
          description: this.$t("initialsetup.step_company_description"),
        },
        {
          value: "3",
          title: this.$t("initialsetup.step_address_title"),
          description: this.$t("initialsetup.step_address_description"),
        },
      ];
    },
  },
  methods: {
    mapSetupLanguageToLocale(languageCode) {
      const normalized = (languageCode ?? "").toLowerCase();
      if (normalized.startsWith("nb") || normalized.startsWith("no")) return "no";
      if (normalized.startsWith("sv")) return "sv";
      if (normalized.startsWith("zh-hk")) return "zh-HK";
      if (normalized.startsWith("zh-cn") || normalized.startsWith("zh")) return "zh-CN";
      return "en";
    },
    setSelectedLanguage(languageCode) {
      this.form.language = languageCode;
      const locale = setStoredLocale(this.mapSetupLanguageToLocale(languageCode));
      this.$i18n.locale = locale;
    },
    goToStep(stepValue) {
      this.currentStep = stepValue;
    },
    getStepState(stepValue) {
      const active = Number(this.currentStep);
      const step = Number(stepValue);
      if (step < active) return "done";
      if (step === active) return "active";
      return "upcoming";
    },
    goToNextStep() {
      this.currentStep = String(Math.min(Number(this.currentStep) + 1, this.steps.length));
    },
    goToPreviousStep() {
      this.currentStep = String(Math.max(Number(this.currentStep) - 1, 1));
    },
    hasFlag(languageCode) {
      return !!this.flagImagesByLanguage[languageCode];
    },
    getFlagSrc(languageCode) {
      return this.flagImagesByLanguage[languageCode];
    },
    openLogoPicker() {
      this.$refs.logoFileInput?.click();
    },
    onLogoFileSelected(event) {
      const file = event?.target?.files?.[0] ?? null;
      this.logoFile = file;
      this.selectedLogoFileName = file?.name ?? "";
      if (this.logoPreviewUrl && this.logoPreviewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(this.logoPreviewUrl);
      }
      this.logoPreviewUrl = file ? URL.createObjectURL(file) : "";
    },

    async loadInitialSetup() {
      this.loading = true;
      try {
        const response = await http().get("/api2/initialsetup");
        const dto = response.data ?? {};
        this.form.companyName = dto.CompanyName ?? "";
        this.form.telephone = dto.Telephone ?? "";
        this.form.email = dto.Email ?? "";
        this.form.addressStreet = dto.AddressStreet ?? "";
        this.form.addressZipAndCity = dto.AddressZipAndCity ?? "";
        this.form.language = dto.Language ?? "en-US";
        this.$i18n.locale = setStoredLocale(this.mapSetupLanguageToLocale(this.form.language));
        this.currentLogoUrl = `/api2/Report/logo?ts=${Date.now()}`;
        if (!this.logoPreviewUrl) {
          this.logoPreviewUrl = this.currentLogoUrl;
        }
      } catch (error) {
        if (error?.status === 204) {
          //setup has been completed previously, redirect to home
          await this.$router.replace({ name: "Home" });
          return;
        }
      } finally {
        this.loading = false;
      }
    },

    async saveSetup() {
      this.saving = true;
      try {
        const formData = new FormData();
        formData.append("CompanyName", this.form.companyName ?? "");
        formData.append("Telephone", this.form.telephone ?? "");
        formData.append("Email", this.form.email ?? "");
        formData.append("AddressStreet", this.form.addressStreet ?? "");
        formData.append("AddressZipAndCity", this.form.addressZipAndCity ?? "");
        formData.append("Language", this.form.language ?? "en-US");

        const file = Array.isArray(this.logoFile) ? this.logoFile[0] : this.logoFile;
        if (file) {
          formData.append("imageFile", file);
        }

        const response = await http().post("/api2/initialsetup", formData);
        if (response?.status === 204) {
          this.openServiceProviderPortal();
          return;
        }
        this.currentLogoUrl = `/api2/Report/logo?ts=${Date.now()}`;
        this.openServiceProviderPortal();
      } finally {
        this.saving = false;
      }
    },

    async skipSetup() {
      this.saving = true;
      try {
        const response = await http().post("/api2/initialsetup/skip");
        if (response?.status === 204 || response?.status === 200) {
          this.openServiceProviderPortal();
        }
      } finally {
        this.saving = false;
      }
    },

    openServiceProviderPortal() {
      window.location.assign("/serviceprovider/#/");
    },
  },
};
</script>

<style scoped>
.initial-setup-page {
  min-height: 100vh;
  background: rgb(var(--v-theme-surface));
}

.setup-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.05), rgba(25, 118, 210, 0.01));
}

.logo-upload-dropzone {
  border: 1px dashed rgba(0, 0, 0, 0.28);
  border-radius: 10px;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.logo-upload-dropzone--has-image {
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.12);
}

.logo-upload-dropzone:hover {
  border-color: rgba(25, 118, 210, 0.7);
  background: rgba(25, 118, 210, 0.02);
}

.logo-upload-preview {
  width: 100%;
}

.logo-upload-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.811);
  color: #6c6c6c;
  text-align: center;
  font-weight: 500;
}

.language-option-card {
  min-height: 112px;
  position: relative;
  cursor: pointer;
  border-color: rgba(0, 0, 0, 0.08) !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.language-option-card:hover {
  border-color: rgba(25, 118, 210, 0.4) !important;
  background: rgba(25, 118, 210, 0.02);
}

.language-option-card--selected {
  border-color: rgb(25, 118, 210) !important;
  background: rgba(25, 118, 210, 0.06);
  box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.2) inset;
}

.language-option-selected-icon {
  position: absolute;
  top: 8px;
  right: 8px;
}

.language-flag-image {
  display: block;
  width: 42px;
  height: 28px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.language-code-fallback {
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  color: rgba(0, 0, 0, 0.68);
  letter-spacing: 0.01em;
}

@media (max-height: 820px) {
  .text-caption {
    line-height: 1.15;
  }
}

@media (min-width: 960px) {
  .onboarding-layout {
    display: grid;
    grid-template-columns: 240px 1fr;
    column-gap: 16px;
    align-items: stretch;
  }
}

.onboarding-sidebar {
  background: linear-gradient(180deg, rgba(25, 118, 210, 0.06), rgba(226, 190, 202, 0.04));
  border: 1px solid rgba(0, 0, 0, 0.04);
  height: 100%;
}

.onboarding-content {
  background: #fff;
}

.onboarding-step-item {
  min-height: 70px;
}

.step-marker-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.step-marker-line {
  width: 1px;
  height: 56px;
  background: rgba(0, 0, 0, 0.15);
  margin-top: 4px;
}
</style>
